import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { ConfigService } from 'src/app/config.service'
import { Observable, of } from 'rxjs'
import { catchError } from 'rxjs/operators'

/**
 * UserSettingsService
 *
 * Provides CRUD operations for user settings.
 * Uses environment-driven base URL via ConfigService to comply with security best practices.
 */
@Injectable({ providedIn: 'root' })
export class UserSettingsService {
  private baseUrl = ''

  public constructor(
    private readonly http: HttpClient,
    private readonly configService: ConfigService
  ) {
    this.baseUrl = `${this.configService.baseUrl}/user-settings`
  }

  /**
   * Fetch user settings by Keycloak user ID
   * @param keycloakId The Keycloak user ID
   */
  public getByKeycloakId(keycloakId: string): Observable<UserSettings | null> {
    return this.http.get<UserSettings>(`${this.baseUrl}/${keycloakId}`).pipe(
      catchError(err => {
        console.warn('getByKeycloakId failed, returning null', { keycloakId, err })
        return of(null)
      })
    )
  }

  /**
   * Create user settings
   * @param payload User settings payload
   */
  public create(payload: UserSettingsCreate): Observable<UserSettings> {
    return this.http.post<UserSettings>(`${this.baseUrl}`, payload)
  }

  /**
   * Update user settings
   * @param id Settings ID
   * @param payload Partial settings to update
   */
  public update(id: number, payload: Partial<UserSettings>): Observable<UserSettings> {
    return this.http.put<UserSettings>(`${this.baseUrl}/${id}`, payload)
  }
}

/**
 * UserSettings
 *
 * Represents persisted user setting entity.
 */
export interface UserSettings {
  id: number
  keycloakId: string
  wantsDailyReport: boolean
}

/**
 * UserSettingsCreate
 *
 * DTO for creating user settings
 */
export interface UserSettingsCreate {
  keycloakId: string
  wantsDailyReport: boolean
}
